import { Injectable } from '@angular/core';
import { environment as ENV } from '../../../environments/environment';
import { SessionStorageService } from './session-storage.service';
import { Observable, catchError, map, switchMap, tap, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { UserLoggedResponse, UserLoginModel } from '../../models/user';
import { Md5 } from 'ts-md5';
import { Router } from '@angular/router';
import { URL_MAP } from '../../shared/urlMap';
import { LocalStorageService } from '../../local-storage/local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private HOST = ENV.HOST;
  private API = URL_MAP.API;
  private KEYS = URL_MAP.KEYS;

  constructor(
    private httpClient: HttpClient,
    private _storageService: SessionStorageService,
    private router: Router,
    private localStorageService: LocalStorageService,
  ) { }

  public logIn(json: UserLoginModel): Observable<any> {
    var jsonParametros = {
      "documentType": json.docType,
      "username": json.docNumber,
      "password": json.password
    };
    return this.httpClient.post(`${this.HOST}${this.API.LOGIN}`, jsonParametros);
  }

  checkTermsAndHD(json: UserLoginModel): Observable<any> {
    var jsonParametros = {
      "pty_tipo_id": json.docType,
      "pty_num_id": json.docNumber,
      "pty_cod_app": "OVA"
    };
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': "*/*"
      })
    };
    return this.httpClient.post(`${this.HOST}${this.API.GETVERSIONTYC_HD}`, jsonParametros, httpOptions);
  }

  loadTermsAndHD(option: number): Observable<any> { //1 "TyC" 2 "HD"
    var jsonParametros = {
      "pty_cod_app": "OVA",
      "pty_tipo_doc": option == 1 ? "TyC" : "HD"
    };
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': "*/*",
      })
    };
    return this.httpClient.post(`${this.HOST}${this.API.GETDATATYC_HD}`, jsonParametros, httpOptions);
  }

  acceptTermsAndHD(keyName:string, versionDoc: string, json: UserLoginModel, dataUser: UserLoggedResponse, ipAddress: string): Observable<any> { //1 "TyC" 2 "HD"
    const pad = (num: number): string => num.toString().padStart(2, '0');
    var jsonParametros = {
      "pty_keyName": keyName,
      "pty_hora": `${new Date().getHours()}:${new Date().getMinutes()}:${new Date().getSeconds()}`,
      "pty_fecha": `${pad(new Date().getDate())}/${pad(new Date().getMonth() + 1)}/${new Date().getFullYear().toString().slice(-2)}`,
      "pty_tipo_doc": json.docType,
      "pty_num_doc": json.docNumber,
      "pty_nom_usu": dataUser.nombre + " " + dataUser.primerApellido,
      "pty_ip": ipAddress,
      "pty_cod_app": "OVA",
      "pty_tipo_usu": "ASESOR",
      "pty_nom_emp": "CMP",
      "pty_version_doc": versionDoc
    };
    const httpOptions = {
      headers: new HttpHeaders({
        'Accept': "*/*"
      })
    };
    return this.httpClient.post(`${this.HOST}${this.API.ACCEPTTYC_HD}`, jsonParametros, httpOptions);
  }

  getToken(): Observable<any> {
    return this.httpClient.post<{ token: string }>(`${this.HOST}${this.API.GETSECURETOKEN}`, {})
      .pipe(
        tap(response => {
          if (!response.token) {
            throw new Error('Token vacío recibido');
          }
          response.token;
        }),
        map(response => response.token),
        catchError(this.handleError)
      );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    if (error instanceof HttpErrorResponse) {
      // Manejar errores HTTP
      console.error('Error HTTP al obtener el token:', error);
      return throwError('No se pudo obtener el token debido a un error en el servidor. Inténtalo nuevamente más tarde.');
    } else {
      // Manejar errores genéricos
      console.error('Error al obtener el token');
      return throwError('No se pudo obtener el token. Inténtalo nuevamente más tarde.');
    }
  }

  getPublicIp(): Observable<{ ip: string }> {
    return this.httpClient.get<{ ip: string }>("https://api.ipify.org?format=json");
  }

  isAuthenticated(): Promise<boolean> {
    return this._storageService.get(`${this.KEYS.AUTHINFO}`).then((resp) => {
      return !!resp; // Devuelve true si resp tiene valor, false si es null o undefined
    });
  }

  public async setAuthInfo(data: any) {
    await this._storageService.set(`${this.KEYS.AUTHINFO}`, data);
    await this.sessionValid(data);
  }

  private async sessionValid(data: any) {
    const md5: any = new Md5();
    const key: any = this.KEYS.KEY;
    await this._storageService.set(key, md5.appendStr(`${key}~${JSON.stringify(data)}~${new Date().getTime()}`).end());
  }

  async logOut() {
    await this._storageService.remove(`${this.KEYS.AUTHINFO}`);
    await this._storageService.remove(`${this.KEYS.KEY}`);
    this.localStorageService.clearRadicacionTrasladosSession();
    this.router.navigate(['/login'])
  }

  public async getAuthInfo() {
    return await this._storageService.get(`${this.KEYS.AUTHINFO}`);
  }
}
